import React, { useEffect, useMemo, useRef } from 'react'
import { useAppSelector } from 'reduxTyped'

import app from 'common/builder/components/chart/FlowChart/app'
import { STATES } from 'common/builder/components/chart/FlowChart/config'
import {
  NODE_BADGE_ID,
  NODE_PLACEHOLDER_ID,
  NODE_OPEN_FLOW_BUTTON_ID,
  NODE_CLEAR_BUTTON_ID,
  NODE_TRIGGER_BUTTON_PREFIX,
  NODE_CREATE_TRIGGER_BUTTON_ID,
  NODE_STATS_TABLE_ITEM,
} from 'common/builder/components/chart/FlowChart/constants'
import { DEFAULT_NODE_WIDTH } from 'common/builder/components/chart/FlowChart/nodes/shared/config'
import ConnectorPoint from 'common/builder/components/chart/FlowChart/nodes/shared/ConnectorPoint/ConnectorPoint'
import * as sharedStyles from 'common/builder/components/chart/FlowChart/nodes/shared/styles'
import { FlowChartPixiShape } from 'common/builder/components/chart/FlowChart/shapes/FlowChartPixiShape'
import { useFlowChartAppState } from 'common/builder/hooks/useFlowChartAppState'
import * as nextStepSelectors from 'common/builder/selectors/builder/nextStepSelectors'
import { IMinimalChangeEvent } from 'constants/constants'

import { InputConnectorPointStyle, NodeContainerDefaultStyles } from './NodeContainer.styles'

type Event = IMinimalChangeEvent & { currentTarget: IMinimalChangeEvent['target'] }

interface NodeContainerProps {
  nodeId: string
  builderId: string
  container: FlowChartPixiShape
  width?: number
  children?: React.ReactNode
  disableFlowChartInteractions: boolean
  highlightColor?: string
  style?: React.CSSProperties
}

export const NodeContainer = ({
  /**
   * used for checking if the connection is allowed and for input connection point
   */
  nodeId,
  /**
   * used for checking the connection is allowed
   */
  builderId,
  children,
  width = DEFAULT_NODE_WIDTH,
  container,
  disableFlowChartInteractions,
  highlightColor,
  style,
}: NodeContainerProps) => {
  /**
   * Disclaimer: logic is copypasted from the main/src/common/builder/components/chart/FlowChart/nodes/shared/NodeCard/NodeCard.tsx
   * and slightly modified to fit the new component structure.
   * Will be redone while refactoring the app.js
   */
  const [appState, { srcId: sourceId }] = useFlowChartAppState()
  const isConnectionAllowed = useAppSelector((state) =>
    nextStepSelectors.isConnectionAllowed(state, builderId, sourceId, nodeId),
  )
  const containerStyle = useMemo(
    () => ({ ...NodeContainerDefaultStyles, width, style }),
    [style, width],
  )

  const highlightColorNumber = highlightColor ? Number(highlightColor.replace('#', '0x')) : null

  const prevAlpha = useRef<null | number>(null)

  useEffect(() => {
    const $container = container

    if (
      appState === STATES.CREATING_NEW_CONNECTOR &&
      app.isNode(container) &&
      !isConnectionAllowed
    ) {
      prevAlpha.current = $container.alpha
      $container.alpha = sharedStyles.Opacity.DISABLED
    } else if (prevAlpha.current) {
      $container.alpha = prevAlpha.current
      prevAlpha.current = null
    }
  }, [isConnectionAllowed, appState, container])

  const processHover = (event: Event) => {
    const target = event?.target
    const targetName = target?.name
    const isConnectionHover = app.state === STATES.CREATING_NEW_CONNECTOR && isConnectionAllowed
    const isAppStateNormal = app.state === STATES.NORMAL
    const isTargetIsCurentTarget = target === event?.currentTarget
    const isNodePlaceholder = targetName === NODE_PLACEHOLDER_ID
    const isNodeBadge = targetName === NODE_BADGE_ID
    const isNodeButton =
      targetName === NODE_CLEAR_BUTTON_ID || targetName === NODE_OPEN_FLOW_BUTTON_ID
    const isBlock = app.isBlock(target)
    const isButton = app.isButton(target)
    const isTrigger =
      typeof targetName === 'string' && targetName.startsWith(NODE_TRIGGER_BUTTON_PREFIX)
    const isCreateTriggerButton = targetName === NODE_CREATE_TRIGGER_BUTTON_ID
    const isNodeStatsTableItem = targetName === NODE_STATS_TABLE_ITEM

    const isNormalHover =
      isAppStateNormal &&
      (isTargetIsCurentTarget ||
        isNodePlaceholder ||
        isNodeBadge ||
        isNodeButton ||
        isBlock ||
        isButton ||
        isTrigger ||
        isCreateTriggerButton ||
        isNodeStatsTableItem)

    const isHover = isNormalHover || isConnectionHover

    if (isHover) {
      const hoveredNode = app.getHoveredNode()
      if (hoveredNode === container) {
        return
      }

      app.setHover(container)
    } else {
      app.dropHover(container)
    }
  }

  const isHoveredRef = useRef<boolean>(false)

  const handleMouseOver = () => {
    isHoveredRef.current = true
  }

  const handleMouseOut = () => {
    isHoveredRef.current = false
    app.dropHover(container)
  }

  const handleMouseMove = (event: Event) => {
    if (!isHoveredRef.current) {
      return
    }

    processHover(event)
  }

  return (
    <view
      name={'nodecard'}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      // eslint-disable-next-line
      // @ts-expect-error
      onMouseMove={handleMouseMove}
      highlightColor={highlightColorNumber}
      style={containerStyle}
      interactive={!disableFlowChartInteractions}
    >
      <ConnectorPoint id={nodeId} style={InputConnectorPointStyle} isTarget />

      {children}
    </view>
  )
}

NodeContainer.displayName = 'NodeContainer'
